import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Tooltip,
  TextField,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';

import { AddCircle, Edit, SearchNormal1, Bag, DocumentCopy } from 'iconsax-react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import useAuth from 'hooks/useAuth';
import Snackbar from 'utils/Snackbar';

import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import CustomTable from 'components/@extended/CustomTable';
import SkeletonTable from 'components/SkeletonTable';
import IconButton from 'components/@extended/IconButton';

import { getAllNOS, deleteNOS } from '../../services/nos.service';

const NOSList = () => {
  const navigate = useNavigate();
  const { organization } = useAuth();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const [size, setSize] = useState(10);
  const [sortArray, setSortArray] = useState<any[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Delete dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedNOS, setSelectedNOS] = useState<any>(null);

  // Fetch NOS
  const fetchNOS = async () => {
    if (!organization?._id) return;

    try {
      setIsLoading(true);

      const reqParams = {
        skip: pageIndex,
        limit: size,
        search
      };

      const res = await getAllNOS(reqParams);

      if (res?.status === 200) {
        console.log(res?.data?.result.NOS);
        setData(res?.data?.result.NOS || res?.data?.data || []);
        setCount(res?.data?.result.NOS?.count || res?.data?.result.NOS?.length || 0);
      }
    } catch (err) {
      console.error('Failed to load NOS:', err);
      Snackbar('Failed to load National Occupational Standards', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (organization?._id) {
      fetchNOS();
    }
  }, [organization?._id, pageIndex, size, search, sortArray]);

  // Sorting handler (if you want to enable sorting later)
  const handleSortChange = (column: any) => {
    const id = column.name || column.id || column.accessor;
    const index = sortArray.findIndex((item) => item.id === id);

    const next = [...sortArray];

    if (index === -1) {
      next.push({ id, sort: true, desc: false });
    } else if (!next[index].desc) {
      next[index].desc = true;
    } else {
      next.splice(index, 1);
    }

    setSortArray(next);
  };

  // Delete handlers
  const handleConfirmDelete = async () => {
    if (!selectedNOS?._id) return;

    try {
      await deleteNOS(selectedNOS._id);
      Snackbar('NOS deleted successfully', 'success');
      setOpenDeleteDialog(false);
      setSelectedNOS(null);
      fetchNOS();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to delete NOS';
      Snackbar(msg, 'error');
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setSelectedNOS(null);
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: 'NOS Code',
        accessor: 'nosCode'
      },
      {
        Header: 'NOS Name',
        accessor: 'nosName'
      },
      {
        Header: 'Version',
        accessor: (row: any) => row?.version || '—'
      },
      {
        Header: 'Created By',
        accessor: (row: any) => {
          const createdBy = row?.createdBy;
          if (!createdBy) return '—';

          const fullName = `${createdBy?.firstName ?? ''} ${createdBy?.lastName ?? ''}`.trim();
          const title = createdBy?.email ?? fullName ?? '—';

          return (
            <Tooltip title={title}>
              <span>{fullName || createdBy?.email || '—'}</span>
            </Tooltip>
          );
        }
      },
      {
        Header: 'Total Marks',
        accessor: 'totalMarks'
      },

      {
        Header: 'Actions',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }: any) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Create new version">
              <IconButton
                color="secondary"
                onClick={() =>
                  navigate('/nos/add', {
                    state: { createNewVersionFrom: row.original }
                  })
                }
              >
                <DocumentCopy />
              </IconButton>
            </Tooltip>
            <Tooltip title={<FormattedMessage id="Edit" />}>
              <IconButton
                color="primary"
                onClick={() =>
                  navigate('/nos/edit', {
                    state: { isEdit: true, data: row.original }
                  })
                }
              >
                <Edit />
              </IconButton>
            </Tooltip>

            <Tooltip title={<FormattedMessage id="Delete" />}>
              <IconButton
                color="error"
                onClick={() => {
                  setSelectedNOS(row.original);
                  setOpenDeleteDialog(true);
                }}
              >
                <Bag />
              </IconButton>
            </Tooltip>
          </Stack>
        )
      }
    ],
    [navigate]
  );

  return (
    <>
      <Box display="flex" flexDirection="column" gap={1.5}>
        <MainCard
          title={
            <Stack direction="row" alignItems="center" spacing={2}>
              <BackToggle show />
              <Box>
                <Typography variant="h5">
                  <FormattedMessage id="NOS List" />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage National Occupational Standards
                </Typography>
              </Box>
            </Stack>
          }
          content={false}
          secondary={
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search NOS..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPageIndex(1);
                    setSearch(searchInput);
                  }
                }}
              />

              <IconButton
                variant="outlined"
                onClick={() => {
                  setPageIndex(1);
                  setSearch(searchInput);
                }}
                disabled={!searchInput}
              >
                <SearchNormal1 />
              </IconButton>

              <IconButton variant="dashed" onClick={() => navigate('/nos/add')}>
                <AddCircle />
              </IconButton>
            </Stack>
          }
        />

        <MainCard content={false}>
          {isLoading ? (
            <SkeletonTable columns={columns} />
          ) : (
            <CustomTable
              columns={columns}
              data={data}
              count={count}
              size={size}
              pageIndex={pageIndex}
              sortArray={['nosCode', 'nosName', 'totalMarks']}
              sort={sortArray}
              setSortingData={handleSortChange}
              handlePageChange={(event: any, page: number) => setPageIndex(page)}
              handleSizeChange={(e: SelectChangeEvent<number>) => {
                setSize(Number(e.target.value));
                setPageIndex(1);
              }}
            />
          )}
        </MainCard>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
        <DialogTitle>Delete NOS</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedNOS?.nosName || selectedNOS?.nosCode || 'this NOS'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NOSList;
